import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ConfigurationService } from '../../services/configuration.service';

interface SupportHoursInterval {
  StartUTC: string | Date;
  EndUTC: string | Date;
}

interface SupportHoursWindow {
  startSeconds: number;
  endSeconds: number;
  startLabel: string;
  endLabel: string;
}

@Component({
  selector: 'shared-support-hours',
  templateUrl: './support-hours.component.html',
  styleUrls: ['./support-hours.component.scss'],
  standalone: false
})
export class SupportHoursComponent implements OnInit, OnChanges {

  @Input() interval: SupportHoursInterval;
  @Input() isCustomer = false;
  @Input() mode: string | null = null;

  isReadingConfig = false;
  outsideSupportHours = false;
  supportHoursSummary: Array<{ Day: string; EndLabel?: string; Hours: string; StartLabel?: string }> = [];
  timeZoneLabel = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Local Time';

  private configLoaded = false;
  private supportHoursByDay: Record<string, SupportHoursWindow | null> = {};

  private readonly dayCodesOrdered = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
  private readonly dayLabelsOrdered = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  private readonly dayCodeByIndex = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  private readonly localTimeFormatter = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  private readonly timeZone = 'America/Los_Angeles';

  constructor(
    private configurationService: ConfigurationService
  ) { }

  ngOnInit(): void {
    this.loadConfiguration();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['interval'] && this.configLoaded) {
      this.evaluateOutsideSupportHours();
    }
  }

  private buildSupportWindow(startValue?: string, endValue?: string): SupportHoursWindow | null {
    const startSeconds = this.parseTimeToSeconds(startValue);
    const endSeconds = this.parseTimeToSeconds(endValue);

    if (startSeconds === null || endSeconds === null) {
      return null;
    }

    return {
      startSeconds,
      endSeconds,
      startLabel: this.formatTimeForDisplay(startValue),
      endLabel: this.formatTimeForDisplay(endValue)
    };
  }

  private evaluateOutsideSupportHours(): void {
    if (!this.configLoaded) {
      this.outsideSupportHours = false;
      return;
    }

    const startDate = this.toDate(this.interval?.StartUTC);
    const endDate = this.toDate(this.interval?.EndUTC);

    if (!startDate || !endDate) {
      this.outsideSupportHours = false;
      return;
    }

    if (endDate.getTime() < startDate.getTime()) {
      this.outsideSupportHours = true;
      return;
    }

    const startParts = this.getZonedParts(startDate);
    const endParts = this.getZonedParts(endDate);

    if (!startParts || !endParts) {
      this.outsideSupportHours = false;
      return;
    }

    const startDayCode = this.dayCodeByIndex[new Date(Date.UTC(startParts.year, startParts.month - 1, startParts.day)).getUTCDay()];
    const endDayCode = this.dayCodeByIndex[new Date(Date.UTC(endParts.year, endParts.month - 1, endParts.day)).getUTCDay()];

    const startWindow = this.supportHoursByDay[startDayCode];
    const endWindow = this.supportHoursByDay[endDayCode];

    if (!startWindow || !endWindow) {
      this.outsideSupportHours = true;
      return;
    }

    const startSeconds = this.partsToSeconds(startParts);
    const endSeconds = this.partsToSeconds(endParts);

    if (startSeconds < startWindow.startSeconds || startSeconds > startWindow.endSeconds) {
      this.outsideSupportHours = true;
      return;
    }

    if (endSeconds < endWindow.startSeconds || endSeconds > endWindow.endSeconds) {
      this.outsideSupportHours = true;
      return;
    }

    this.outsideSupportHours = false;
  }

  private extractConfigRecords(result: any): Array<{ KeyName?: string; Value?: string }> {
    if (!result) {
      return [];
    }

    const value = result?.Value ?? result?.value ?? result;
    return Array.isArray(value) ? value : [];
  }

  private formatTimeForDisplay(value?: string): string {
    const match = value ? /^([0-1]\d|2[0-3]):([0-5]\d):([0-5]\d)$/.exec(value.trim()) : null;
    if (!match) {
      return '';
    }

    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    const seconds = Number(match[3]);

    const todayParts = this.getZonedPartsForZone(new Date(), this.timeZone);
    if (!todayParts) {
      return '';
    }

    // Use today's date in LA and interpret the time in LA, then render in local timezone.
    const reference = this.getDateForZoneTime(todayParts.year, todayParts.month, todayParts.day, hours, minutes, seconds, this.timeZone);
    if (!reference) {
      return '';
    }

    return this.localTimeFormatter.format(reference);
  }

  private getDateForZoneTime(year: number, month: number, day: number, hour: number, minute: number, second: number, timeZone: string): Date | null {
    try {
      const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
      const parts = this.getZonedPartsForZone(utcGuess, timeZone);
      if (!parts) {
        return null;
      }

      const zonedAsUTC = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
      const offsetMs = zonedAsUTC - utcGuess.getTime();
      return new Date(utcGuess.getTime() - offsetMs);
    } catch (error) {
      return null;
    }
  }

  private getZonedParts(date: Date): { year: number; month: number; day: number; hour: number; minute: number; second: number } | null {
    return this.getZonedPartsForZone(date, this.timeZone);
  }

  private getZonedPartsForZone(date: Date, timeZone: string): { year: number; month: number; day: number; hour: number; minute: number; second: number } | null {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });

      const parts = formatter.formatToParts(date);
      const lookup = (type: string) => Number(parts.find(part => part.type === type)?.value ?? NaN);

      const year = lookup('year');
      const month = lookup('month');
      const day = lookup('day');
      const hour = lookup('hour');
      const minute = lookup('minute');
      const second = lookup('second');

      if ([year, month, day, hour, minute, second].some(value => Number.isNaN(value))) {
        return null;
      }

      return { year, month, day, hour, minute, second };
    } catch (error) {
      return null;
    }
  }

  private loadConfiguration(): void {
    this.isReadingConfig = true;

    this.configurationService.readAll('SUPPORT-HOURS-')
      .pipe(catchError(() => of(null)))
      .subscribe((result) => {
        const dayValues: Record<string, { start?: string; end?: string }> = {};
        const records = this.extractConfigRecords(result);

        records.forEach((record) => {
          const key = record?.KeyName;
          const value = record?.Value;
          if (typeof key !== 'string' || typeof value !== 'string') {
            return;
          }

          const match = /^SUPPORT-HOURS-(SUN|MON|TUE|WED|THU|FRI|SAT)-(START|END)-TIME$/.exec(key);
          if (!match) {
            return;
          }

          const dayCode = match[1];
          const kind = match[2] as 'START' | 'END';

          if (!dayValues[dayCode]) {
            dayValues[dayCode] = {};
          }

          if (kind === 'START') {
            dayValues[dayCode].start = value;
          } else {
            dayValues[dayCode].end = value;
          }
        });

        this.supportHoursByDay = {};
        this.supportHoursSummary = this.dayCodesOrdered.map((dayCode, index) => {
          const label = this.dayLabelsOrdered[index];
          const startValue = dayValues[dayCode]?.start;
          const endValue = dayValues[dayCode]?.end;
          const window = this.buildSupportWindow(startValue, endValue);

          this.supportHoursByDay[dayCode] = window;

          if (!window) {
            return { Day: label, Hours: 'Closed' };
          }

          return {
            Day: label,
            EndLabel: window.endLabel,
            Hours: `${window.startLabel} - ${window.endLabel}`,
            StartLabel: window.startLabel
          };
        });

        this.configLoaded = true;
        this.isReadingConfig = false;
        this.evaluateOutsideSupportHours();
      });
  }

  private parseTimeToSeconds(value?: string): number | null {
    if (!value) {
      return null;
    }

    const match = /^([0-1]\d|2[0-3]):([0-5]\d):([0-5]\d)$/.exec(value.trim());
    if (!match) {
      return null;
    }

    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    const seconds = Number(match[3]);
    return hours * 3600 + minutes * 60 + seconds;
  }

  private partsToSeconds(parts: { hour: number; minute: number; second: number }): number {
    return parts.hour * 3600 + parts.minute * 60 + parts.second;
  }


  private toDate(value: string | Date | undefined): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
}
